// src/components/admin/DashboardOverview.jsx
// ─────────────────────────────────────────────────────────
//  WHY getAllCars() instead of getStats():
//  The Rails /admin/cars/stats endpoint returns 0 for total_cars
//  because it may filter by a specific criteria.
//  getAllCars() is reliable — we compute every stat client-side
//  from the full list so numbers always match the Cars tab.
//
//  Car source distinction:
//    pricing_status === 'accepted'  → came from a seller (sell request → inspection → priced)
//    pricing_status === 'admin'     → admin created directly
//
//  FIX (2024): Inspection count was showing 0 because getAllInspections()
//  may return { car_inspections: [...] } — added that key to the unwrap
//  logic, and added getWorkflowInspections() as a fallback so the total
//  always matches what the Inspections Management tab shows.
// ─────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  adminCarService,
  adminUserService,
  adminSellRequestService,
  adminPaymentService,
  adminTestDriveService,
  adminInspectionService,
} from '../../services/adminService';

/* ─── animated counter ───────────────────────────────────── */
function useCountUp(target, duration = 1100) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    cancelAnimationFrame(raf.current);
    if (!target) { setVal(0); return; }
    let start = null;
    const tick = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(p * target));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return val;
}

/* ─── KPI card ───────────────────────────────────────────── */
const KpiCard = ({ title, rawValue, subtitle, icon, gradient, accent, delay = 0, prefix = '' }) => {
  const n = useCountUp(typeof rawValue === 'number' ? rawValue : 0, 1100);
  return (
    <div className="db-kpi" style={{ ...S.kpiCard, background: gradient, animationDelay: `${delay}ms` }}>
      <div style={{ ...S.kpiIconBox, background: accent + '22' }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <p style={S.kpiLabel}>{title}</p>
        <p style={{ ...S.kpiNum, color: accent }}>{prefix}{n.toLocaleString('en-IN')}</p>
        <p style={S.kpiSub}>{subtitle}</p>
      </div>
      <div style={{ ...S.kpiRing, borderColor: accent + '18' }} />
    </div>
  );
};

/* ─── horizontal progress bar ───────────────────────────── */
const Bar = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>
          {value} <span style={{ color: '#d1d5db', fontWeight: 400 }}>({pct}%)</span>
        </span>
      </div>
      <div style={{ height: 7, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 1s ease' }} />
      </div>
    </div>
  );
};

/* ─── funnel row ─────────────────────────────────────────── */
const FRow = ({ icon, label, value, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 13px', background: '#f8fafc', borderRadius: 9 }}>
    <span style={{ fontSize: 18 }}>{icon}</span>
    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</span>
    <span style={{ fontSize: 22, fontWeight: 900, color }}>{value}</span>
  </div>
);

/* ─── action button ──────────────────────────────────────── */
const Btn = ({ icon, label, color, bg, onClick }) => (
  <button
    style={{ ...S.actionBtn, background: bg, borderColor: color + '30' }}
    onClick={onClick}
    onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.transform = 'translateX(4px)'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = color + '30'; e.currentTarget.style.transform = 'none'; }}
  >
    <span style={{ fontSize: 17, width: 22, textAlign: 'center' }}>{icon}</span>
    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color }}>{label}</span>
    <span style={{ fontSize: 12, fontWeight: 700, color }}>→</span>
  </button>
);

/* ─── source badge ───────────────────────────────────────── */
const SrcBadge = ({ label, value, color, bg }) => (
  <div style={{ flex: 1, padding: '14px 16px', background: bg, borderRadius: 11, borderLeft: `4px solid ${color}` }}>
    <p style={{ fontSize: 22, fontWeight: 900, color, margin: '0 0 3px' }}>{value}</p>
    <p style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</p>
  </div>
);

/* ── helper: unwrap inspection list from any API response shape ──
   Rails may return any of:
     plain array          → [...]
     { inspections: [...] }
     { car_inspections: [...] }   ← this was the missing case
     { data: [...] }
*/
function unwrapInspections(res) {
  if (Array.isArray(res))                        return res;
  if (Array.isArray(res?.car_inspections))       return res.car_inspections;
  if (Array.isArray(res?.inspections))           return res.inspections;
  if (Array.isArray(res?.data))                  return res.data;
  return [];
}

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
const DashboardOverview = ({ setActiveTab }) => {
  const [d, setD] = useState({
    // ── cars (always from getAllCars) ──
    totalCars: 0,
    availableCars: 0,
    soldCars: 0,
    reservedCars: 0,
    draftCars: 0,
    liveCars: 0,
    // car source breakdown
    sellerCars: 0,
    adminCars: 0,
    totalRevenue: 0,
    // ── users ──
    totalUsers: 0,
    // ── sell requests ──
    totalRequests: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    rejectedRequests: 0,
    // ── inspections ──
    totalInspections: 0,
    pendingInspections: 0,
    completedInspections: 0,
    // ── test drives ──
    totalTestDrives: 0,
    pendingTestDrives: 0,
  });
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const go = (tab) => { if (typeof setActiveTab === 'function') setActiveTab(tab); };

  /* ── data loader ────────────────────────────────────────── */
  const load = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    const errs = [];
    const s = {};
    const acts = [];

    /* ── 1. CARS ─────────────────────────────────────────── */
    try {
      const res = await adminCarService.getAllCars();
      let list = [];
      if (Array.isArray(res))             list = res;
      else if (Array.isArray(res?.cars))  list = res.cars;
      else if (Array.isArray(res?.data))  list = res.data;

      s.totalCars     = list.length;
      s.availableCars = list.filter(c => c.status === 'available').length;
      s.soldCars      = list.filter(c => c.status === 'sold').length;
      s.reservedCars  = list.filter(c => c.status === 'reserved').length;
      s.draftCars     = list.filter(c => c.listing_status === 'draft').length;
      s.liveCars      = list.filter(c => c.listing_status === 'live').length;

      s.sellerCars = list.filter(c =>
        c.pricing_status === 'accepted' ||
        c.pricing_status === 'inspection_done' ||
        c.pricing_status === 'priced'
      ).length;
      s.adminCars = list.filter(c =>
        c.pricing_status === 'admin' ||
        (c.owned_by_platform === true && c.pricing_status !== 'accepted')
      ).length;

      s.totalRevenue = list
        .filter(c => c.status === 'sold')
        .reduce((sum, c) => sum + +(c.selling_price ?? c.price ?? 0), 0);

      [...list]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3)
        .forEach(c => acts.push({
          icon: '🚗',
          title: `${c.brand ?? ''} ${c.model ?? ''} ${c.year ?? ''}`.trim() || c.title || 'Car',
          sub: `₹${(+(c.price ?? 0)).toLocaleString('en-IN')} · ${c.location ?? ''} · ${c.listing_status ?? ''}`,
          badge: c.status || 'draft',
          badgeColor: c.status === 'available' ? '#10b981' : c.status === 'sold' ? '#6b7280' : '#f59e0b',
          time: fmtTime(c.created_at),
        }));
    } catch (e) {
      console.error('getAllCars failed:', e);
      errs.push('Car data');
    }

    /* ── 2. USERS ─────────────────────────────────────────── */
    try {
      const res = await adminUserService.getAllUsers();
      const list = Array.isArray(res) ? res : (res?.users ?? res?.data ?? []);
      s.totalUsers = list.length;
    } catch (_) { errs.push('Users'); }

    /* ── 3. SELL REQUESTS ─────────────────────────────────── */
    try {
      const res = await adminSellRequestService.getAllSellRequests();
      const list = Array.isArray(res) ? res : (res?.data ?? res?.sell_requests ?? []);
      s.totalRequests    = list.length;
      s.pendingRequests  = list.filter(r => !r.approval_status || r.approval_status === 'pending').length;
      s.approvedRequests = list.filter(r => r.approval_status === 'approved').length;
      s.rejectedRequests = list.filter(r => r.approval_status === 'rejected').length;

      [...list]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 2)
        .forEach(r => acts.push({
          icon: '📋',
          title: `${r.brand ?? r.car_brand ?? ''} ${r.model ?? r.car_model ?? ''} sell request`.trim() || 'Sell request',
          sub: `by ${r.user?.name ?? r.owner_name ?? 'User'}${r.city ? ' · ' + r.city : ''}`,
          badge: r.approval_status || 'pending',
          badgeColor: r.approval_status === 'approved' ? '#10b981'
                    : r.approval_status === 'rejected' ? '#ef4444'
                    : '#f59e0b',
          time: fmtTime(r.created_at),
        }));
    } catch (_) { errs.push('Sell requests'); }

    /* ── 4. PAYMENTS — optional ───────────────────────────── */
    try {
      const list = await adminPaymentService.getAllPayments();
      if (!s.totalRevenue) {
        s.totalRevenue = Array.isArray(list)
          ? list.reduce((a, p) => a + +(p.amount ?? 0), 0)
          : 0;
      }
    } catch (_) { /* silent */ }

    /* ── 5. INSPECTIONS ───────────────────────────────────────
       FIX: Rails returns { car_inspections: [...] } from this
       endpoint. The previous code only checked for `inspections`
       and `data` keys, so the list was always empty → count = 0.

       Strategy:
         a) Try getAllInspections() and unwrap with all known keys.
         b) If that still gives 0, fall back to getWorkflowInspections()
            which is what the Inspections tab uses — guarantees the
            dashboard total always matches what the admin sees there.
    ──────────────────────────────────────────────────────── */
    try {
      let list = [];

      try {
        const res = await adminInspectionService.getAllInspections();
        list = unwrapInspections(res);
        console.log('[Dashboard] getAllInspections raw keys:', res ? Object.keys(res) : 'null', '→ list length:', list.length);
      } catch (e1) {
        console.warn('[Dashboard] getAllInspections failed, trying workflow endpoint:', e1.message);
      }

      // Fallback: if primary returned 0 items, try the workflow endpoint
      if (list.length === 0) {
        try {
          const res2 = await adminInspectionService.getWorkflowInspections();
          const fallback = unwrapInspections(res2);
          console.log('[Dashboard] getWorkflowInspections fallback → list length:', fallback.length);
          if (fallback.length > 0) list = fallback;
        } catch (e2) {
          console.warn('[Dashboard] getWorkflowInspections also failed:', e2.message);
        }
      }

      s.totalInspections = list.length;

      // pending = not yet completed/approved/rejected
      s.pendingInspections = list.filter(i =>
        ['pending', 'scheduled', 'assigned', 'in_progress'].includes(i.status)
      ).length;

      // completed = done (includes cancelled so admin sees full picture)
      s.completedInspections = list.filter(i =>
        ['completed', 'approved', 'priced', 'confirmed'].includes(i.status)
      ).length;

    } catch (_) { /* silent — partial data banner will show */ }

    /* ── 6. TEST DRIVES ───────────────────────────────────── */
    try {
      const res = await adminTestDriveService.getAllTestDrives();
      const list = Array.isArray(res) ? res : (res?.test_drives ?? res?.data ?? []);
      s.totalTestDrives   = list.length;
      s.pendingTestDrives = list.filter(t => ['pending', 'requested'].includes(t.status)).length;
    } catch (_) { /* silent */ }

    if (!mounted.current) return;
    setD(prev => ({ ...prev, ...s }));
    setActivity(
      acts
        .filter(a => a.title && a.title.trim())
        .slice(0, 6)
    );
    setErrors(errs);
    setUpdatedAt(new Date());
    setLoading(false);
    setRefreshing(false);
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  /* ── helpers ─────────────────────────────────────────────── */
  function fmtTime(ts) {
    if (!ts) return '—';
    try {
      const mins = Math.round((Date.now() - new Date(ts)) / 60000);
      if (mins < 60)   return `${mins}m ago`;
      if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
      return new Date(ts).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch { return '—'; }
  }

  const invTotal  = d.totalCars || 1;
  const sellTotal = d.pendingRequests + d.approvedRequests + d.rejectedRequests;

  /* ── loading ─────────────────────────────────────────────── */
  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 480, gap: 14 }}>
      <div style={{ width: 52, height: 52, border: '5px solid #e5e7eb', borderTopColor: '#FFD60A', borderRightColor: '#003566', borderRadius: '50%', animation: 'dbSpin 0.85s linear infinite' }} />
      <p style={{ fontSize: 16, fontWeight: 700, color: '#003566', margin: 0 }}>Loading dashboard…</p>
      <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>Fetching live data from all services</p>
      <style>{`@keyframes dbSpin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div style={S.root}>
      <style>{`
        @keyframes dbFadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes dbSpin    { to{transform:rotate(360deg)} }
        @keyframes dbPulse   { 0%,100%{opacity:1} 50%{opacity:.35} }
        .db-kpi { animation: dbFadeUp .42s ease both; transition: transform .2s, box-shadow .2s; }
        .db-kpi:hover { transform:translateY(-5px)!important; box-shadow:0 16px 40px rgba(0,0,0,.15)!important; }
      `}</style>

      {/* ── HEADER ──────────────────────────────────── */}
      <div style={S.hdr}>
        <div>
          <h1 style={S.hdrH1}>Dashboard Overview</h1>
          <p style={S.hdrSub}>
            Real-time platform analytics
            {updatedAt && <span style={{ color: '#9ca3af' }}> · Updated {updatedAt.toLocaleTimeString()}</span>}
          </p>
        </div>
        <button
          style={{ ...S.refreshBtn, opacity: refreshing ? 0.7 : 1 }}
          onClick={() => load(true)}
          disabled={refreshing}
        >
          <span style={{ display: 'inline-block', animation: refreshing ? 'dbSpin 1s linear infinite' : 'none' }}>↻</span>
          {refreshing ? ' Syncing…' : ' Refresh'}
        </button>
      </div>

      {/* ── ERROR BANNER ─────────────────────────────── */}
      {errors.length > 0 && (
        <div style={S.errBanner}>
          ⚠️ &nbsp;Partial data ({errors.join(', ')}) — other stats are live.
          <button style={S.errBtn} onClick={() => load(true)}>Retry</button>
        </div>
      )}

      {/* ══ KPI ROW ════════════════════════════════════ */}
      <div style={S.kpiGrid}>
        {[
          { title: 'Total Cars',    rawValue: d.totalCars,        subtitle: `${d.availableCars} available · ${d.soldCars} sold`,                          icon: '🚗', gradient: 'linear-gradient(135deg,#003566,#001d3d)', accent: '#FFD60A',  delay: 0 },
          { title: 'Total Users',   rawValue: d.totalUsers,       subtitle: 'Registered on platform',                                                     icon: '👥', gradient: 'linear-gradient(135deg,#0f172a,#1e3a5f)', accent: '#60a5fa',  delay: 65 },
          { title: 'Sell Requests', rawValue: sellTotal,          subtitle: `${d.pendingRequests} pending · ${d.approvedRequests} approved`,               icon: '📋', gradient: 'linear-gradient(135deg,#78350f,#92400e)', accent: '#fbbf24',  delay: 130 },
          { title: 'Revenue',       rawValue: d.totalRevenue,     subtitle: `${d.soldCars} cars sold`,                                                    icon: '💰', gradient: 'linear-gradient(135deg,#064e3b,#065f46)', accent: '#34d399',  delay: 195, prefix: '₹' },
          { title: 'Inspections',   rawValue: d.totalInspections, subtitle: `${d.pendingInspections} pending · ${d.completedInspections} done`,            icon: '🔍', gradient: 'linear-gradient(135deg,#312e81,#4338ca)', accent: '#a78bfa',  delay: 260 },
          { title: 'Test Drives',   rawValue: d.totalTestDrives,  subtitle: `${d.pendingTestDrives} pending approval`,                                    icon: '🏁', gradient: 'linear-gradient(135deg,#7f1d1d,#991b1b)', accent: '#f87171',  delay: 325 },
        ].map((k, i) => <KpiCard key={i} {...k} />)}
      </div>

      {/* ══ CAR SOURCE ROW ═════════════════════════════ */}
      <div style={S.sourceRow}>
        <div style={S.sourceCard}>
          <h3 style={S.sourceTitle}>🚗 Car Inventory Breakdown</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <SrcBadge label="From Sellers"   value={d.sellerCars}  color="#10b981" bg="#f0fdf4" />
            <SrcBadge label="Admin Created"  value={d.adminCars}   color="#3b82f6" bg="#eff6ff" />
            <SrcBadge label="Live Listings"  value={d.liveCars}    color="#FFD60A" bg="#fffbeb" />
            <SrcBadge label="Draft Listings" value={d.draftCars}   color="#9ca3af" bg="#f9fafb" />
          </div>
          <Bar label="Available" value={d.availableCars} total={invTotal} color="#10b981" />
          <Bar label="Sold"      value={d.soldCars}      total={invTotal} color="#6b7280" />
          <Bar label="Reserved"  value={d.reservedCars}  total={invTotal} color="#f59e0b" />
          <Bar label="Draft"     value={d.draftCars}     total={invTotal} color="#9ca3af" />
        </div>

        {/* Sell request funnel */}
        <div style={S.sourceCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={S.sourceTitle}>📋 Sell Request Funnel</h3>
            <button style={S.lnkBtn} onClick={() => go('sell-requests')}>Manage →</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <FRow icon="📥" label="Total Received" value={sellTotal}            color="#60a5fa" />
            <FRow icon="⏳" label="Pending Review"  value={d.pendingRequests}   color="#fbbf24" />
            <FRow icon="✅" label="Approved"         value={d.approvedRequests} color="#34d399" />
            <FRow icon="❌" label="Rejected"         value={d.rejectedRequests} color="#f87171" />
          </div>
        </div>

        {/* Operations */}
        <div style={S.sourceCard}>
          <h3 style={{ ...S.sourceTitle, marginBottom: 14 }}>⚙️ Operations</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            {[
              { icon: '🔍', label: 'Pending Inspections',  v: d.pendingInspections,   t: d.totalInspections, c: '#a78bfa', tab: 'inspections' },
              { icon: '✅', label: 'Done Inspections',      v: d.completedInspections, t: d.totalInspections, c: '#34d399', tab: 'inspections' },
              { icon: '🏁', label: 'Pending Test Drives',   v: d.pendingTestDrives,    t: d.totalTestDrives,  c: '#fb923c', tab: 'test-drives'  },
            ].map((op, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '6px 0' }} onClick={() => go(op.tab)}>
                <span style={{ fontSize: 17, width: 24, textAlign: 'center' }}>{op.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>{op.label}</div>
                  <div style={{ height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: op.t > 0 ? `${Math.round((op.v / op.t) * 100)}%` : '0%', background: op.c, borderRadius: 3, transition: 'width 1s ease' }} />
                  </div>
                </div>
                <span style={{ fontSize: 19, fontWeight: 900, color: op.c, minWidth: 26, textAlign: 'right' }}>{op.v}</span>
              </div>
            ))}
          </div>

          {/* Revenue box */}
          <div style={S.revBox}>
            <p style={S.revLabel}>💳 Total Revenue</p>
            <p style={S.revNum}>₹{d.totalRevenue.toLocaleString('en-IN')}</p>
            <p style={S.revSub}>{d.soldCars} cars sold successfully</p>
          </div>
        </div>
      </div>

      {/* ══ BOTTOM: ACTIONS + ACTIVITY ════════════════ */}
      <div style={S.btmRow}>

        {/* Quick Actions */}
        <div style={{ ...S.card, flex: '0 0 288px' }}>
          <h3 style={{ ...S.sourceTitle, marginBottom: 13 }}>⚡ Quick Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <Btn icon="➕" label="Add New Car"       color="#FFD60A" bg="#001d3d"  onClick={() => go('cars')} />
            <Btn icon="📋" label="Sell Requests"     color="#fbbf24" bg="#78350f"  onClick={() => go('sell-requests')} />
            <Btn icon="🔍" label="Inspections"       color="#a78bfa" bg="#1e1b4b"  onClick={() => go('inspections')} />
            <Btn icon="💲" label="Pricing"           color="#34d399" bg="#022c22"  onClick={() => go('pricing')} />
            <Btn icon="👤" label="Users"             color="#60a5fa" bg="#0c1a2e"  onClick={() => go('users')} />
            <Btn icon="🏁" label="Test Drives"       color="#f87171" bg="#450a0a"  onClick={() => go('test-drives')} />
            <Btn icon="🚚" label="Pickup Requests"   color="#fb923c" bg="#431407"  onClick={() => go('pickup-requests')} />
            <Btn icon="📦" label="Delivery Requests" color="#38bdf8" bg="#082f49"  onClick={() => go('delivery-requests')} />
            <Btn icon="💳" label="Payments"          color="#c084fc" bg="#2e1065"  onClick={() => go('payments')} />
            <Btn icon="🔄" label="Workflow"          color="#34d399" bg="#052e16"  onClick={() => go('workflow')} />
          </div>
        </div>

        {/* Recent Activity */}
        <div style={{ ...S.card, flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={S.sourceTitle}>🕐 Recent Activity</h3>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#10b981', fontWeight: 700 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', display: 'inline-block', animation: 'dbPulse 1.5s ease-in-out infinite' }} />
              Live
            </span>
          </div>

          {activity.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '36px 0', gap: 8 }}>
              <span style={{ fontSize: 40 }}>📭</span>
              <p style={{ color: '#9ca3af', margin: 0, fontSize: 14 }}>No recent activity yet</p>
              <p style={{ color: '#d1d5db', margin: 0, fontSize: 12 }}>Activity will appear here as users interact with the platform</p>
            </div>
          ) : (
            <div>
              {activity.map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 0', borderBottom: i < activity.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: a.badgeColor + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>
                    {a.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.title}</p>
                    <p style={{ fontSize: 11, color: '#9ca3af', margin: '2px 0 0' }}>{a.sub}</p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                    <span style={{ padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: a.badgeColor + '18', color: a.badgeColor, textTransform: 'capitalize' }}>{a.badge}</span>
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>{a.time}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Platform health bar */}
          <div style={{ display: 'flex', marginTop: 18, paddingTop: 14, borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
            {[
              { label: 'Total Cars',   val: d.totalCars,  color: '#FFD60A' },
              { label: 'Live Cars',    val: d.liveCars,   color: '#10b981' },
              { label: 'Users',        val: d.totalUsers, color: '#60a5fa' },
              { label: 'Requests',     val: sellTotal,    color: '#fbbf24' },
              { label: 'Revenue',      val: d.totalRevenue > 0 ? `₹${(d.totalRevenue / 100000).toFixed(1)}L` : '₹0', color: '#34d399' },
            ].map((h, i, arr) => (
              <div key={i} style={{ flex: 1, textAlign: 'center', padding: '0 6px', borderRight: i < arr.length - 1 ? '1px solid #f1f5f9' : 'none', minWidth: 64, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 18, fontWeight: 900, color: h.color }}>{h.val}</span>
                <span style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── styles ──────────────────────────────────────────────── */
const S = {
  root: { padding: '0 0 44px', fontFamily: "'Segoe UI',system-ui,sans-serif" },

  hdr:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 10 },
  hdrH1:     { fontSize: 26, fontWeight: 800, color: '#003566', margin: '0 0 3px', letterSpacing: '-0.4px' },
  hdrSub:    { fontSize: 13, color: '#6b7280', margin: 0 },
  refreshBtn:{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', background: '#003566', color: '#FFD60A', border: '2px solid #FFD60A44', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' },

  errBanner: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: '#fffbeb', border: '1.5px solid #fbbf24', borderRadius: 9, marginBottom: 20, fontSize: 12, color: '#78350f', flexWrap: 'wrap' },
  errBtn:    { marginLeft: 'auto', padding: '4px 12px', background: '#003566', color: '#fff', border: 'none', borderRadius: 5, fontWeight: 600, fontSize: 11, cursor: 'pointer' },

  kpiGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14, marginBottom: 18 },
  kpiCard:   { padding: '18px 16px', borderRadius: 14, display: 'flex', alignItems: 'flex-start', gap: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.11)', position: 'relative', overflow: 'hidden', cursor: 'default' },
  kpiIconBox:{ width: 46, height: 46, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 },
  kpiLabel:  { fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.7px', margin: '0 0 4px' },
  kpiNum:    { fontSize: 28, fontWeight: 900, margin: '0 0 3px', lineHeight: 1 },
  kpiSub:    { fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: 0 },
  kpiRing:   { position: 'absolute', right: -20, bottom: -20, width: 80, height: 80, borderRadius: '50%', border: '18px solid', pointerEvents: 'none' },

  sourceRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 16, marginBottom: 18 },
  sourceCard:{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' },
  sourceTitle:{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 14px' },

  lnkBtn:    { fontSize: 12, color: '#003566', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0 },

  revBox:    { marginTop: 16, padding: '14px 16px', background: 'linear-gradient(135deg,#003566,#001d3d)', borderRadius: 11, border: '1.5px solid #FFD60A33' },
  revLabel:  { fontSize: 9, fontWeight: 700, color: '#FFD60A99', textTransform: 'uppercase', letterSpacing: '0.7px', margin: '0 0 4px' },
  revNum:    { fontSize: 24, fontWeight: 900, color: '#FFD60A', margin: '0 0 2px', lineHeight: 1 },
  revSub:    { fontSize: 10, color: 'rgba(255,255,255,0.4)', margin: 0 },

  btmRow:    { display: 'flex', gap: 16, flexWrap: 'wrap' },
  card:      { background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' },

  actionBtn: { display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', border: '1.5px solid', borderRadius: 9, cursor: 'pointer', transition: 'all 0.18s ease', textAlign: 'left', width: '100%' },
};

export default DashboardOverview;